import type { AuditEventType, AuditLogEntry } from '@/types/candidates';
import {
  Archive,
  ArchiveRestore,
  BadgeCheck,
  Briefcase,
  Calendar,
  CalendarClock,
  CalendarX,
  CheckCircle2,
  CircleDot,
  Copy,
  FileUp,
  MessageSquare,
  MoveRight,
  Pencil,
  Plus,
  RefreshCw,
  Shield,
  ShieldAlert,
  Tag,
  Trash2,
  UserCheck,
  UserCog,
  UserPlus,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';

export interface AuditFilterCategory {
  key: 'all' | 'stage' | 'skills' | 'files' | 'gdpr';
  label: string;
  types: AuditEventType[];
}

export const AUDIT_FILTERS: AuditFilterCategory[] = [
  { key: 'all', label: 'All events', types: [] },
  {
    key: 'stage',
    label: 'Stage changes',
    types: ['ASSIGNMENT_STAGE_CHANGED', 'ASSIGNMENT_RESULT_CHANGED', 'CANDIDATE_STATUS_CHANGED'],
  },
  { key: 'skills', label: 'Skill verifications', types: ['SKILL_VERIFIED'] },
  { key: 'files', label: 'Files', types: ['FILE_UPLOADED', 'FILE_DELETED'] },
  { key: 'gdpr', label: 'GDPR', types: ['GDPR_CHANGED', 'CANDIDATE_ANONYMISED'] },
];

export const EVENT_ICONS: Record<AuditEventType, LucideIcon> = {
  CANDIDATE_CREATED: UserPlus,
  CANDIDATE_UPDATED: Pencil,
  CANDIDATE_ARCHIVED: Archive,
  CANDIDATE_ANONYMISED: ShieldAlert,
  ASSIGNMENT_CREATED: Briefcase,
  ASSIGNMENT_STAGE_CHANGED: MoveRight,
  ASSIGNMENT_RESULT_CHANGED: CheckCircle2,
  CANDIDATE_STATUS_CHANGED: UserCheck,
  SKILL_ADDED: Plus,
  SKILL_VERIFIED: CheckCircle2,
  SKILL_REMOVED: X,
  TAG_ADDED: Tag,
  TAG_REMOVED: Tag,
  FILE_UPLOADED: FileUp,
  FILE_DELETED: Trash2,
  INTERVIEW_SCHEDULED: Calendar,
  INTERVIEW_RESCHEDULED: CalendarClock,
  INTERVIEW_CANCELED: CalendarX,
  NOTE_ADDED: MessageSquare,
  GDPR_CHANGED: Shield,
  VACANCY_STATUS_CHANGED: RefreshCw,
  VACANCY_DUPLICATED: Copy,
  VACANCY_ARCHIVED: Archive,
  VACANCY_RESTORED: ArchiveRestore,
  CANDIDATE_CONVERTED_TO_EMPLOYEE: BadgeCheck,
  CANDIDATE_RESPONSIBLE_RECRUITER_CHANGED: UserCog,
};

// The backend AuditEventType enum can grow ahead of this frontend union (e.g.
// CANDIDATE_CONVERTED_TO_EMPLOYEE shipped on the backend before the renderer
// knew about it). Resolve through a fallback so an unmapped type degrades to a
// neutral icon instead of rendering `undefined` and crashing the whole feed.
export function getEventIcon(eventType: string): LucideIcon {
  return (EVENT_ICONS as Partial<Record<string, LucideIcon>>)[eventType] ?? CircleDot;
}

function actorName(entry: AuditLogEntry): string {
  if (!entry.actor) return 'System';
  return `${entry.actor.firstName} ${entry.actor.lastName}`.trim() || 'Unknown';
}

function getPayloadString(payload: Record<string, unknown>, key: string): string | null {
  const v = payload[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function getPayloadNumber(payload: Record<string, unknown>, key: string): number | null {
  const v = payload[key];
  return typeof v === 'number' ? v : null;
}

interface ChangedField {
  field: string;
  previous: string | number | null;
  next: string | number | null;
}

function getChangedFields(payload: Record<string, unknown>): ChangedField[] {
  const raw = payload.changedFields;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((f): f is ChangedField => {
      if (!f || typeof f !== 'object') return false;
      const obj = f as Record<string, unknown>;
      return typeof obj.field === 'string';
    })
    .map((f) => ({
      field: f.field,
      previous: (f as ChangedField).previous ?? null,
      next: (f as ChangedField).next ?? null,
    }));
}

function describeStatusChange(previous: unknown, next: unknown): ReactNode {
  const prev = typeof previous === 'string' ? previous : '—';
  const nxt = typeof next === 'string' ? next : '—';
  return (
    <>
      changed status <strong>{prev}</strong> → <strong>{nxt}</strong>
    </>
  );
}

export function renderEventDescription(entry: AuditLogEntry): ReactNode {
  const name = actorName(entry);
  const p = entry.payload ?? {};

  switch (entry.eventType) {
    case 'CANDIDATE_CREATED': {
      const source = getPayloadString(p, 'source');
      const skills = getPayloadNumber(p, 'skillsCount') ?? 0;
      const tags = getPayloadNumber(p, 'tagsCount') ?? 0;
      return (
        <>
          <strong>{name}</strong> created candidate profile
          {source ? <> from <em>{source}</em></> : null}
          {skills > 0 || tags > 0 ? (
            <span className="text-muted-foreground"> ({skills} skills · {tags} tags)</span>
          ) : null}
        </>
      );
    }

    case 'CANDIDATE_UPDATED': {
      const fields = getChangedFields(p);
      if (fields.length === 0) {
        return <><strong>{name}</strong> updated candidate profile</>;
      }
      if (fields.length === 1 && fields[0].field === 'status') {
        return (
          <>
            <strong>{name}</strong> {describeStatusChange(fields[0].previous, fields[0].next)}
          </>
        );
      }
      const names = fields.map((f) => f.field).join(', ');
      return (
        <>
          <strong>{name}</strong> updated <em>{names}</em>
        </>
      );
    }

    case 'CANDIDATE_ARCHIVED': {
      const prev = getPayloadString(p, 'previousStatus');
      return (
        <>
          <strong>{name}</strong> archived candidate
          {prev ? <span className="text-muted-foreground"> (was {prev})</span> : null}
        </>
      );
    }

    case 'CANDIDATE_ANONYMISED':
      return <><strong>{name}</strong> anonymised candidate data (GDPR Art. 17)</>;

    case 'ASSIGNMENT_CREATED': {
      const vacancy = getPayloadString(p, 'vacancyTitle') ?? 'a vacancy';
      const source = getPayloadString(p, 'assignmentSource');
      return (
        <>
          <strong>{name}</strong> assigned candidate to <em>{vacancy}</em>
          {source ? <span className="text-muted-foreground"> ({source})</span> : null}
        </>
      );
    }

    case 'ASSIGNMENT_STAGE_CHANGED': {
      const vacancy = getPayloadString(p, 'vacancyTitle') ?? 'vacancy';
      const from = getPayloadString(p, 'fromStage') ?? '—';
      const to = getPayloadString(p, 'toStage') ?? '—';
      return (
        <>
          <strong>{name}</strong> moved candidate to <strong>{to}</strong> on <em>{vacancy}</em>
          <span className="text-muted-foreground"> ({from} → {to})</span>
        </>
      );
    }

    case 'ASSIGNMENT_RESULT_CHANGED': {
      const vacancy = getPayloadString(p, 'vacancyTitle') ?? 'vacancy';
      const result = getPayloadString(p, 'result') ?? '—';
      const reason = getPayloadString(p, 'rejectionReason');
      return (
        <>
          <strong>{name}</strong> set result to <strong>{result}</strong> on <em>{vacancy}</em>
          {reason ? <span className="text-muted-foreground"> — {reason}</span> : null}
        </>
      );
    }

    case 'CANDIDATE_STATUS_CHANGED': {
      const from = getPayloadString(p, 'from') ?? '—';
      const to = getPayloadString(p, 'to') ?? '—';
      const reason = getPayloadString(p, 'reason');
      return (
        <>
          <strong>{name}</strong> changed status to <strong>{to.toLowerCase()}</strong>
          <span className="text-muted-foreground"> ({from.toLowerCase()} → {to.toLowerCase()})</span>
          {reason === 'stage_auto_mapping' ? (
            <span className="text-muted-foreground"> · auto from stage</span>
          ) : null}
        </>
      );
    }

    case 'CANDIDATE_CONVERTED_TO_EMPLOYEE': {
      const rejectedOthers = getPayloadNumber(p, 'rejectedOthers') ?? 0;
      return (
        <>
          <strong>{name}</strong> converted candidate to an employee
          {rejectedOthers > 0 ? (
            <span className="text-muted-foreground">
              {' '}(closed {rejectedOthers} other {rejectedOthers === 1 ? 'application' : 'applications'})
            </span>
          ) : null}
        </>
      );
    }

    case 'SKILL_ADDED': {
      const count = getPayloadNumber(p, 'count') ?? 1;
      const source = getPayloadString(p, 'source') ?? 'manual';
      const names = getPayloadString(p, 'skillNames');
      return (
        <>
          <strong>{name}</strong> added {count} skill{count === 1 ? '' : 's'} via {source}
          {names ? <span className="text-muted-foreground"> ({names})</span> : null}
        </>
      );
    }

    case 'SKILL_VERIFIED': {
      const skillName = getPayloadString(p, 'skillName') ?? 'skill';
      return <><strong>{name}</strong> verified skill <em>{skillName}</em></>;
    }

    case 'SKILL_REMOVED': {
      const skillName = getPayloadString(p, 'skillName') ?? 'skill';
      return <><strong>{name}</strong> removed skill <em>{skillName}</em></>;
    }

    case 'TAG_ADDED': {
      const tag = getPayloadString(p, 'name') ?? 'tag';
      return <><strong>{name}</strong> added tag <em>#{tag}</em></>;
    }

    case 'TAG_REMOVED': {
      const tag = getPayloadString(p, 'name') ?? 'tag';
      return <><strong>{name}</strong> removed tag <em>#{tag}</em></>;
    }

    case 'FILE_UPLOADED': {
      const filename = getPayloadString(p, 'filename') ?? 'file';
      const fileType = getPayloadString(p, 'fileType');
      const version = getPayloadNumber(p, 'version');
      return (
        <>
          <strong>{name}</strong> uploaded <em>{filename}</em>
          {fileType || version ? (
            <span className="text-muted-foreground">
              {' '}({fileType ?? '?'}{version ? ` v${version}` : ''})
            </span>
          ) : null}
        </>
      );
    }

    case 'FILE_DELETED': {
      const filename = getPayloadString(p, 'filename') ?? 'file';
      return <><strong>{name}</strong> deleted <em>{filename}</em></>;
    }

    case 'INTERVIEW_SCHEDULED': {
      const when = getPayloadString(p, 'scheduledAt');
      return (
        <>
          <strong>{name}</strong> scheduled an interview
          {when ? <> on <em>{when}</em></> : null}
        </>
      );
    }

    case 'INTERVIEW_RESCHEDULED': {
      const when = getPayloadString(p, 'newScheduledAt');
      const toWindow = p.flippedToWindow === true;
      return (
        <>
          <strong>{name}</strong> rescheduled an interview
          {toWindow ? <> to a flexible window</> : when ? <> to <em>{when}</em></> : null}
        </>
      );
    }

    case 'INTERVIEW_CANCELED':
      return <><strong>{name}</strong> canceled an interview</>;

    case 'VACANCY_STATUS_CHANGED': {
      const from = getPayloadString(p, 'from');
      const to = getPayloadString(p, 'to') ?? '—';
      return (
        <>
          <strong>{name}</strong> changed vacancy status to <strong>{to.toLowerCase()}</strong>
          {from ? (
            <span className="text-muted-foreground"> ({from.toLowerCase()} → {to.toLowerCase()})</span>
          ) : null}
        </>
      );
    }

    case 'VACANCY_DUPLICATED': {
      const sourceTitle = getPayloadString(p, 'sourceTitle');
      return (
        <>
          <strong>{name}</strong> duplicated{' '}
          {sourceTitle ? <>the vacancy <em>{sourceTitle}</em></> : <>a vacancy</>}
        </>
      );
    }

    case 'VACANCY_ARCHIVED': {
      const prev = getPayloadString(p, 'from');
      return (
        <>
          <strong>{name}</strong> archived the vacancy
          {prev ? <span className="text-muted-foreground"> (was {prev.toLowerCase()})</span> : null}
        </>
      );
    }

    case 'VACANCY_RESTORED': {
      const to = getPayloadString(p, 'to');
      return (
        <>
          <strong>{name}</strong> restored the vacancy
          {to ? <span className="text-muted-foreground"> (to {to.toLowerCase()})</span> : null}
        </>
      );
    }

    case 'NOTE_ADDED': {
      const label =
        getPayloadString(p, 'noteType') === 'INTERVIEW_FEEDBACK'
          ? 'interview feedback'
          : 'internal';
      const mentions = getPayloadNumber(p, 'mentionsCount') ?? 0;
      return (
        <>
          <strong>{name}</strong> added an <em>{label}</em> note
          {mentions > 0 ? (
            <span className="text-muted-foreground"> (mentioned {mentions})</span>
          ) : null}
        </>
      );
    }

    case 'GDPR_CHANGED': {
      const newStatus = getPayloadString(p, 'newStatus') ?? 'updated';
      const source = getPayloadString(p, 'source');
      return (
        <>
          <strong>{name}</strong> set GDPR status to <strong>{newStatus.toLowerCase()}</strong>
          {source ? <span className="text-muted-foreground"> (source: {source})</span> : null}
        </>
      );
    }

    case 'CANDIDATE_RESPONSIBLE_RECRUITER_CHANGED': {
      const added = Array.isArray(p.added) ? (p.added as Array<{ firstName?: unknown; lastName?: unknown }>) : [];
      const removed = Array.isArray(p.removed) ? (p.removed as Array<{ firstName?: unknown; lastName?: unknown }>) : [];
      const fmt = (e: { firstName?: unknown; lastName?: unknown }) =>
        [e.firstName, e.lastName].filter((v) => typeof v === 'string').join(' ').trim() || 'someone';
      const addedNames = added.map(fmt).join(', ');
      const removedNames = removed.map(fmt).join(', ');
      if (added.length > 0 && removed.length === 0) {
        return (
          <>
            <strong>{name}</strong> assigned <em>{addedNames}</em> as Responsible Recruiter
          </>
        );
      }
      if (removed.length > 0 && added.length === 0) {
        return (
          <>
            <strong>{name}</strong> removed <em>{removedNames}</em> from Responsible Recruiters
          </>
        );
      }
      return (
        <>
          <strong>{name}</strong> updated Responsible Recruiters
          <span className="text-muted-foreground">
            {' '}(added <em>{addedNames || '—'}</em>, removed <em>{removedNames || '—'}</em>)
          </span>
        </>
      );
    }

    default:
      return <><strong>{name}</strong> triggered {entry.eventType}</>;
  }
}
